import { useColumbiaErrors } from '@ben/columbia/contexts';
import Input, { InputVariant } from '@ben/orion/input';
import { useCallback, useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { fieldCannotBeBlankErrorMessage } from '../../components/shared/constants';

interface UseMinMaxInputProps {
  componentControllerName: string;
  form: UseFormReturn;
  minFieldName?: string;
  maxFieldName?: string;
  validationMessage?: {
    min: string;
    max: string;
  };
}

interface MinMaxInputProps {
  isMin: boolean;
  name: string;
  hintText?: string;
  inputProps?: Record<string, unknown>;
}

const useMinMaxInput = ({
  componentControllerName,
  form,
  minFieldName = 'min',
  maxFieldName = 'max',
  validationMessage,
}: UseMinMaxInputProps) => {
  const { getFirstErrorMessage } = useColumbiaErrors();

  const minFormulaError = getFirstErrorMessage(
    `${componentControllerName}.${minFieldName}Formula`
  );
  const maxFormulaError = getFirstErrorMessage(
    `${componentControllerName}.${maxFieldName}Formula`
  );

  const { control, setValue, watch } = form;

  const [isFixedNumber, setIsFixedNumber] = useState<boolean>(true);

  const minFormula = useWatch({
    name: `${componentControllerName}.${minFieldName}Formula`,
    exact: true,
  }) as string | undefined;

  const validateMinMax = useCallback(
    (value: number | string, isMin: boolean) => {
      if (value === '' || value === null || value === undefined)
        return fieldCannotBeBlankErrorMessage;

      if (!isFixedNumber) return true;

      const valueToCompare = watch(
        `${componentControllerName}.${isMin ? maxFieldName : minFieldName}`
      ) as string | number | null | undefined;
      if (
        valueToCompare === '' ||
        valueToCompare === null ||
        valueToCompare === undefined
      )
        return true;

      const numberValue = Number(value);
      const numberCompareValue = Number(valueToCompare);

      if (isMin) {
        return numberValue > numberCompareValue ? validationMessage?.min : true;
      }

      return numberValue < numberCompareValue ? validationMessage?.max : true;
    },
    [
      componentControllerName,
      watch,
      isFixedNumber,
      minFieldName,
      maxFieldName,
      validationMessage,
    ]
  );

  const handleFixedNumberChange = useCallback(
    (value: boolean) => {
      setIsFixedNumber(value);
      if (value) {
        setValue(`${componentControllerName}.${minFieldName}Formula`, '');
        setValue(`${componentControllerName}.${maxFieldName}Formula`, '');
      } else {
        setValue(`${componentControllerName}.${minFieldName}`, '');
        setValue(`${componentControllerName}.${maxFieldName}`, '');
      }
    },
    [componentControllerName, setValue, minFieldName, maxFieldName]
  );

  useEffect(() => {
    if (minFormula) {
      setIsFixedNumber(false);
    }
  }, [minFormula]);

  const renderMinMaxInput = useCallback(
    ({ isMin, name, hintText, inputProps = {} }: MinMaxInputProps) => {
      const currentFieldName = isMin ? minFieldName : maxFieldName;
      const fieldName = `${componentControllerName}.${currentFieldName}${
        isFixedNumber ? '' : 'Formula'
      }`;
      const formulaError = isMin ? minFormulaError : maxFormulaError;
      const serverError = isFixedNumber ? undefined : formulaError;

      return (
        <Controller
          key={fieldName}
          name={fieldName}
          control={control}
          render={({ field, fieldState }) => {
            const hasError = !!fieldState.error || !!serverError;
            const errorMessage = fieldState.error?.message ?? serverError;

            return (
              <Input
                name={name}
                hintText={hintText}
                type={isFixedNumber ? 'number' : 'text'}
                value={
                  (field.value as string | number | null | undefined) ?? ''
                }
                onChange={(event) => {
                  if (isFixedNumber) {
                    const raw = event.target.value;
                    field.onChange(raw === '' ? '' : Number(raw));
                    return;
                  }
                  field.onChange(event);
                }}
                onBlur={field.onBlur}
                variant={
                  isFixedNumber ? InputVariant.Text : InputVariant.TextArea
                }
                height={isFixedNumber ? undefined : '76px'}
                hasError={hasError}
                errorMessage={errorMessage}
                {...inputProps}
              />
            );
          }}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
            validate: (value) =>
              validateMinMax(value as string | number, isMin),
          }}
        />
      );
    },
    [
      componentControllerName,
      control,
      isFixedNumber,
      minFormulaError,
      maxFormulaError,
      validateMinMax,
      minFieldName,
      maxFieldName,
    ]
  );

  return {
    isFixedNumber,
    setIsFixedNumber: handleFixedNumberChange,
    renderMinMaxInput,
  };
};

export default useMinMaxInput;
